import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import TemplateDetailModal from '../Modal/TemplateDetailModal';

const SubTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem;
  margin: 0;
`;

const SubTableRow = styled.tr`
  border-bottom: 1px solid #E5E7EB;

  &:last-child {
    border-bottom: none;
  }
`;

const SubTableCell = styled.td`
  padding: 0.4rem 0.3rem;
  font-size: 0.7rem;
  color: #374151;
  text-align: center;
  vertical-align: top;
`;

const SubTableHeaderCell = styled.th`
  padding: 0.4rem 0.3rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #6B7280;
  text-align: center;
  background-color: #F9FAFB;
`;

const EditTemplateModal = ({ isOpen, onClose, templateData, recordId, selectedRecord, onSave }) => {
  const [selectedFilters, setSelectedFilters] = useState(['ตรวจสุขภาพ', 'ปีล่าสุด']);
  const [templateName, setTemplateName] = useState('');
  const [departmentFrom, setDepartmentFrom] = useState('เลือก');
  const [forGroup, setForGroup] = useState('เลือก');
  const [tags, setTags] = useState('');
  const [templateSelections, setTemplateSelections] = useState({});
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedTemplateData, setSelectedTemplateData] = useState(null);
  const [masterData, setMasterData] = useState(null);

  const isRadioParameter = (parameterName) => {
    const radioParams = [
      'Protein', 'Glucose', 'Ketones', 'Blood', 'Leukocytes', 'Nitrite', 'Bilirubin', 'Urobilinogen',
      'Color', 'Clarity',
      'Color (Stool)', 'Appearance (Stool)', 'WBC (Stool)', 'RBC (Stool)', 'Parasite',
      'RBC Index',
      'HBsAb', 'HBsAg', 'HCV Ab', 'Hemoglobin detection stool',
    ];
    return radioParams.includes(parameterName);
  };

  const isNegPosParameter = (parameterName) => {
    const negPosParams = ['HBsAb', 'HBsAg', 'HCV Ab', 'Hemoglobin detection stool'];
    return negPosParams.includes(parameterName);
  };

  const NEG_POS_OPTIONS = ['Negative', 'Positive'];

  useEffect(() => {
    if (isOpen) {
      const loadMasterData = async () => {
        try {
          const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost/mfu-backend';
          const response = await fetch(`${API_BASE_URL}/api/reference/parameters`);

          if (response.ok) {
            const result = await response.json();
            if (result.status === 'success' && result.data) {
              setMasterData(result.data);
            }
          }
        } catch (error) {
          console.error('Error loading master data:', error);
        }
      };
      loadMasterData();
    }
  }, [isOpen]);

  const getNormalRangeFromMaster = (paramName, department, ageRange) => {
    if (!masterData) return '< 200';

    let sex = 'F,M';
    if (department === 'เพศชาย') sex = 'M';
    else if (department === 'เพศหญิง') sex = 'F';

    let age = 30;
    const ageMatch = ageRange?.match(/(\d+)/);
    if (ageMatch) {
      age = parseInt(ageMatch[1]);
    }

    for (const category in masterData) {
      const categoryData = masterData[category];

      let searchName = paramName;
      if (paramName === 'CHO') searchName = 'คอเลสเตอรอลรวม';
      else if (paramName === 'TRI') searchName = 'ไตรกลีเซอไรด์';
      else if (paramName === 'FBS') searchName = 'Glucose';
      else if (paramName === 'GLU') searchName = 'Glucose';

      if (categoryData[searchName]) {
        const ranges = categoryData[searchName];
        for (const range of ranges) {
          const sexMatch = range.sex.includes(sex) || range.sex === 'F,M';
          const ageMatch = age >= range.age_min && age <= range.age_max;

          if (sexMatch && ageMatch) {
            return range.normal_range;
          }
        }
      }
    }
    return '< 200';
  };

  const mockTemplateData = [
    {
      id: 1,
      templateName: 'ตรวจสุขภาพทั่วไป',
      createdBy: 'นพ. นพพงศ์ ถนอมศรี',
      tags: ['ตรวจสุขภาพ', 'ปีล่าสุด'],
      department: 'ทั้งสองเพศ',
      ageRange: '18-60',
      results: {
        CHO: { normal: '< 200', abnormal: '240 - 260' },
        TRI: { normal: '< 150', abnormal: '160 - 189' },
        HDL: { normal: '> 40', abnormal: '20 - 35' },
        LDL: { normal: '< 100', abnormal: '200 - 499' }
      },
      isSelected: true
    },
    {
      id: 2,
      templateName: 'ตรวจไขมันในเลือด',
      createdBy: 'นพ. นพพงศ์ ถนอมศรี',
      tags: ['ไขมัน', 'คอเลสเตอรอล'],
      department: 'ทั้งสองเพศ',
      ageRange: '25-65',
      results: {
        CHO: { normal: '< 200', abnormal: '240 - 260' },
        TRI: { normal: '< 150', abnormal: '160 - 189' },
        HDL: { normal: '> 40', abnormal: '20 - 35' },
        LDL: { normal: '< 100', abnormal: '200 - 499' }
      },
      isSelected: false
    },
    {
      id: 3,
      templateName: 'ตรวจเบาหวาน',
      createdBy: 'นพ. สมหญิง ดูแลดี',
      tags: ['เบาหวาน', 'น้ำตาล'],
      department: 'ทั้งสองเพศ',
      ageRange: '30-70',
      results: {
        FBS: { normal: '70-100', abnormal: '126-200' },
        HbA1C: { normal: '< 7.0', abnormal: '7.0-9.0' },
        GLU: { normal: '< 140', abnormal: '140-200' }
      },
      isSelected: false
    }
  ];

  useEffect(() => {
    const initialStates = {};
    mockTemplateData.forEach(item => {
      initialStates[item.id] = item.isSelected;
    });
    setTemplateSelections(initialStates);
  }, []);

  const removeFilter = (filterToRemove) => {
    setSelectedFilters(prev => prev.filter(filter => filter !== filterToRemove));
  };

  const handleCheckboxChange = (templateId, checked) => {
    setTemplateSelections(prev => ({
      ...prev,
      [templateId]: checked
    }));
  };

  const handleTagClick = (filterName) => {
    const templateToShow = mockTemplateData.find(template =>
      template.templateName.includes(filterName) ||
      template.tags.some(tag => tag.includes(filterName))
    );

    if (templateToShow) {
      const formattedData = {
        templateName: templateToShow.templateName,
        createdBy: templateToShow.createdBy,
        tags: templateToShow.tags,
        gender: templateToShow.department,
        ageRange: templateToShow.ageRange,
        parameters: Object.keys(templateToShow.results),
        normalValues: Object.values(templateToShow.results).map(result => result.normal),
        setValues: Object.values(templateToShow.results).map(result => result.abnormal)
      };
      
      setSelectedTemplateData(formattedData);
      setIsDetailModalOpen(true);
    }
  };

  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false);
    setSelectedTemplateData(null);
  };

  const handleSave = async () => {
    if (onSave) await onSave();
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        <div style={{
          padding: '24px 32px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{
            fontSize: '25px',
            fontWeight: '400',
            margin: 0,
            color: '#000000'
          }}>
            แก้ไข Template ผลสุขภาพ
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#6B7280'
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '32px' }}>
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '500',
              marginBottom: '16px',
              textAlign: 'center',
              color: '#171717'
            }}>
              การแปลผลสุขภาพ
            </h3>
            
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              {selectedFilters.map((filter, index) => (
                <div
                  key={index}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: 'transparent',
                    border: '1px solid #00B5B8',
                    borderRadius: '0px',
                    padding: '4px 8px',
                    fontSize: '14px',
                    color: '#00B5B8',
                    position: 'relative',
                    width: 'fit-content',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleTagClick(filter)}
                >
                  <span style={{ marginRight: '8px' }}>{filter}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFilter(filter);
                    }}
                    style={{
                      background: 'white',
                      border: '1px solid #00B5B8',
                      borderRadius: '50%',
                      width: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      color: '#00B5B8'
                    }}
                  >
                    <X size={8} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr 1fr auto auto',
            gridTemplateRows: 'auto auto auto',
            gap: '16px',
            alignItems: 'end',
            marginBottom: '16px'
          }}>
            <div style={{ gridColumn: '1', gridRow: '1' }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#000000',
                display: 'block',
                marginBottom: '8px'
              }}>
                ค้นหาชื่อ Template
              </label>
            </div>
            <div style={{ gridColumn: '2', gridRow: '1' }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#000000',
                display: 'block',
                marginBottom: '8px'
              }}>
                ค้นหาจากผู้สร้าง Template
              </label>
            </div>
            <div style={{ gridColumn: '3', gridRow: '1' }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#000000',
                display: 'block',
                marginBottom: '8px'
              }}>
                สำหรับเพศ
              </label>
            </div>
            <div style={{ gridColumn: '4', gridRow: '1' }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#000000',
                display: 'block',
                marginBottom: '8px'
              }}>
                ป้ายกำกับ Tag
              </label>
            </div>
            
            <div style={{ gridColumn: '1', gridRow: '2' }}>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="พิมพ์ชื่อค้นหา"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #CFD3D4',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div style={{ gridColumn: '2', gridRow: '2' }}>
              <select
                value={departmentFrom}
                onChange={(e) => setDepartmentFrom(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">เลือก</option>
                <option value="doctor1">นพ. นพพงศ์ ถนอมศรี</option>
                <option value="doctor2">นพ. สมหญิง ดูแลดี</option>
              </select>
            </div>
            
            <div style={{ gridColumn: '3', gridRow: '2' }}>
              <select
                value={forGroup}
                onChange={(e) => setForGroup(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">เลือก</option>
                <option value="male">เพศชาย</option>
                <option value="female">เพศหญิง</option>
                <option value="both">ทั้งสองเพศ</option>
              </select>
            </div>
            
            <div style={{ gridColumn: '4', gridRow: '2' }}>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="พิมพ์ tag ใหม่"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #CFD3D4',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ gridColumn: '5', gridRow: '2' }}>
              <button style={{
                backgroundColor: '#FBFFFF',
                color: '#9c9e9eff',
                border: '1px solid #CFD3D4',
                borderRadius: '6px',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                height: 'fit-content'
              }}>
                เพิ่ม
              </button>
            </div>
            
            <div style={{ gridColumn: '6', gridRow: '2' }}>
              <button style={{
                backgroundColor: '#00B5B8',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '6px',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                height: 'fit-content'
              }}>
                ค้นหา
              </button>
            </div>

            <div style={{ gridColumn: '4', gridRow: '3' }}>
              <div style={{
                display: 'flex',
                gap: '8px',
                marginTop: '8px',
                fontSize: '14px'
              }}>
                <span 
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #00B5B8',
                    borderRadius: '0px',
                    padding: '4px 8px',
                    color: '#00B5B8',
                    position: 'relative',
                    width: 'fit-content',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleTagClick('ตรวจสุขภาพ')}
                >
                  ตรวจสุขภาพ
                  <button 
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      background: 'white',
                      border: '1px solid #00B5B8',
                      borderRadius: '50%',
                      width: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      color: '#00B5B8'
                    }}
                  >
                    <X size={8} />
                  </button>
                </span>
                <span 
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #00B5B8',
                    borderRadius: '0px',
                    padding: '4px 8px',
                    color: '#00B5B8',
                    position: 'relative',
                    width: 'fit-content',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleTagClick('ปีล่าสุด')}
                >
                  ปีล่าสุด
                  <button 
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      background: 'white',
                      border: '1px solid #00B5B8',
                      borderRadius: '50%',
                      width: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      color: '#00B5B8'
                    }}
                  >
                    <X size={8} />
                  </button>
                </span>
              </div>
            </div>
          </div>


          <hr style={{ border: '1px solid #E5E7EB', margin: '24px 0' }} />

          <div style={{
            overflowX: 'auto',
            border: '1px solid #E5E7EB',
            borderRadius: '8px'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
              <thead style={{ backgroundColor: '#F9FAFB' }}>
                <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <th rowSpan="2" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>ที่</th>
                  <th rowSpan="2" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>Template</th>
                  <th rowSpan="2" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>สร้างโดย</th>
                  <th rowSpan="2" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>ป้ายกำกับ</th>
                  <th rowSpan="2" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>สำหรับเพศ</th>
                  <th rowSpan="2" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>ช่วงอายุ (ปี)</th>
                  <th colSpan="3" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'center'
                  }}>ผลตรวจสุขภาพ</th>
                  <th rowSpan="2" style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6F6F6F',
                    textAlign: 'center'
                  }}>เลือก</th>
                </tr>
                <tr>
                  <th style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>พารามิเตอร์</th>
                  <th style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>ค่าปกติ</th>
                  <th style={{
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#6F6F6F',
                    textAlign: 'left'
                  }}>ค่าที่กำหนด</th>
                </tr>
              </thead>
              <tbody>
                {mockTemplateData.map((item) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                    <td style={{
                      padding: '16px',
                      fontSize: '14px',
                      color: '#374151',
                      verticalAlign: 'top',
                      fontWeight: '500'
                    }}>{item.id}</td>
                    <td style={{
                      padding: '16px',
                      fontSize: '14px',
                      color: '#374151',
                      verticalAlign: 'top'
                    }}>{item.templateName}</td>
                    <td style={{
                      padding: '16px',
                      fontSize: '14px',
                      color: '#374151',
                      verticalAlign: 'top'
                    }}>{item.createdBy}</td>
                    <td style={{
                      padding: '16px',
                      fontSize: '14px',
                      color: '#374151',
                      verticalAlign: 'top'
                    }}>
                      {item.tags.map((tag, index) => (
                        <div key={index}>• {tag}</div>
                      ))}
                    </td>
                    <td style={{
                      padding: '16px',
                      fontSize: '14px',
                      color: '#374151',
                      verticalAlign: 'top'
                    }}>{item.department}</td>
                    <td style={{
                      padding: '16px',
                      fontSize: '14px',
                      color: '#374151',
                      verticalAlign: 'top'
                    }}>{item.ageRange}</td>
                    <td style={{
                      padding: '16px',
                      fontSize: '14px',
                      color: '#374151',
                      verticalAlign: 'top'
                    }}>
                      {Object.keys(item.results).map((param, index) => (
                        <div key={index}>{param}</div>
                      ))}
                    </td>
                    <td style={{
                      padding: '16px',
                      fontSize: '14px',
                      color: '#374151',
                      verticalAlign: 'top'
                    }}>
                      {Object.keys(item.results).map((param, index) => {
                        const allNormalRanges = item.results[param].allNormalRanges || [];

                        if (allNormalRanges.length > 0) {
                          return (
                            <div key={index} style={{ marginBottom: '8px' }}>
                              <SubTable>
                                <thead>
                                  <tr>
                                    <SubTableHeaderCell>เพศ</SubTableHeaderCell>
                                    <SubTableHeaderCell>อายุ</SubTableHeaderCell>
                                    <SubTableHeaderCell>ค่าปกติ</SubTableHeaderCell>
                                  </tr>
                                </thead>
                                <tbody>
                                  {allNormalRanges.map((range, idx) => (
                                    <SubTableRow key={idx}>
                                      <SubTableCell>{range.sex}</SubTableCell>
                                      <SubTableCell>{range.age_range}</SubTableCell>
                                      <SubTableCell>{range.normal_range}</SubTableCell>
                                    </SubTableRow>
                                  ))}
                                </tbody>
                              </SubTable>
                            </div>
                          );
                        } else {
                          return <div key={index}>{getNormalRangeFromMaster(param, item.department, item.ageRange)}</div>;
                        }
                      })}
                    </td>
                    <td style={{
                      padding: '16px',
                      fontSize: '14px',
                      color: '#374151',
                      verticalAlign: 'top'
                    }}>
                      {Object.keys(item.results).map((param, index) => {
                        const values = item.results[param];
                        const isNegPos = isNegPosParameter(param);
                        const isRadio = isRadioParameter(param);

                        if (isNegPos) {
                          return (
                            <div key={index}>
                              <select
                                value={values.radioValue || values.abnormal || 'Negative'}
                                onChange={() => {}}
                                style={{ padding: '0.25rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.8rem', cursor: 'pointer' }}
                              >
                                {NEG_POS_OPTIONS.map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            </div>
                          );
                        } else if (isRadio) {
                          return <div key={index}>{values.radioValue || values.abnormal || '-'}</div>;
                        } else {
                          return <div key={index}>{values.abnormal}</div>;
                        }
                      })}
                    </td>
                    <td style={{
                      padding: '16px',
                      fontSize: '14px',
                      color: '#374151',
                      verticalAlign: 'top',
                      textAlign: 'center'
                    }}>
                      <input
                        type="checkbox"
                        checked={templateSelections[item.id] || false}
                        onChange={(e) => handleCheckboxChange(item.id, e.target.checked)}
                        style={{
                          width: '16px',
                          height: '16px',
                          accentColor: '#00B5B8'
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '32px'
          }}>
            <button
              onClick={handleCancel}
              style={{
                backgroundColor: 'white',
                color: '#374151',
                border: '1px solid #D1D5DB',
                borderRadius: '4px',
                padding: '8px 24px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              ยกเลิก
            </button>
            <button
              onClick={handleSave}
              style={{
                backgroundColor: '#00B5B8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 24px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              บันทึก
            </button>
          </div>
        </div>
      </div>

      {isDetailModalOpen && (
        <div style={{ zIndex: 1100 }}>
          <TemplateDetailModal
            isOpen={isDetailModalOpen}
            onClose={handleDetailModalClose}
            templateData={selectedTemplateData}
          />
        </div>
      )}
    </div>
  );
};

export default EditTemplateModal;